import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import { getLandingCms } from '../../redux/features/landingCmsSlice';
import SectionPanel from './landing/SectionPanel';
import SeoPanel from './landing/SeoPanel';
import SettingsPanel from './landing/SettingsPanel';
import TestimonialsPanel from './landing/TestimonialsPanel';
import { CMS_ROUTES, PUBLIC_LANDING_URL, siblingPath } from './cmsNav';

/**
 * Landing Page CMS — the public page at /auth/parent-landing.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS SCREEN OWNS, AND WHAT IT DELIBERATELY DOES NOT
 *
 * OWNS: every word and picture a visitor reads, all the meta tags, the
 * structured data, the Google Tag Manager slot, the WhatsApp number and the
 * testimonial videos.
 *
 * DOES NOT OWN: the design. Colours, gradients, the organic blob shapes, the
 * float animations and the layout coordinates are not editable here and are not
 * going to be. Each card's visual treatment comes from the component's own
 * design array and is matched to the card by POSITION, so an editor can rewrite
 * every word on the page and reorder every card without being able to make it
 * look broken. That constraint is what makes this screen safe to hand to
 * someone who has never seen the code.
 *
 * ---------------------------------------------------------------------------
 * THE FOUR TABS ARE FOUR DIFFERENT JOBS
 *
 *   Content       what the page says. The most-used tab by far.
 *   SEO           what Google and a shared WhatsApp link show. Its own tab
 *                 because it has its own audience — nobody edits a meta
 *                 description while rewriting hero copy.
 *   Testimonials  purpose-built because a video has fields nothing else has.
 *   Settings      GTM, the logo, the WhatsApp button. Set once, rarely touched.
 */

const TABS = [
  { value: 'content', label: 'Content', icon: 'mdi:text-box-edit-outline' },
  { value: 'seo', label: 'SEO & Meta', icon: 'mdi:google' },
  { value: 'testimonials', label: 'Testimonials', icon: 'mdi:video-outline' },
  { value: 'settings', label: 'Settings', icon: 'mdi:cog-outline' },
];

const LandingPageCms = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    page,
    sections,
    meta,
    metaPreview,
    settings,
    testimonials,
    options,
    revalidateSeconds,
    loading,
  } = useSelector((state) => state.landingCms);

  const [tab, setTab] = useState('content');

  const load = () => dispatch(getLandingCms('parent-landing'));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notSeeded = !loading && !page;

  return (
    <GlobalWrapper title="Landing Page">
      {loading && !page && <CircularProgress size={24} />}

      {notSeeded && (
        <Alert severity="warning">
          No CMS rows for this page yet. Apply <code>prisma/sql/010_landing_cms.sql</code> and{' '}
          <code>013_landing_cms_seed.sql</code> in the student portal repo. Until then the live
          page renders its built-in copy, so nothing is broken — it is simply not editable.
        </Alert>
      )}

      {page && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Editing <strong>{page.name}</strong>
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<Icon icon="mdi:clock-outline" width={14} />}
                  label={`Changes go live within ${revalidateSeconds}s`}
                />
                {meta?.robots?.startsWith('noindex') && (
                  <Chip size="small" color="warning" label="Hidden from search engines" />
                )}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon icon="mdi:refresh" width={16} />}
                onClick={load}
              >
                Reload
              </Button>
              {/* The other half of this CMS. An employee rewriting the landing
                  copy routinely wants the papers behind its funnel. */}
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon icon="mdi:file-document-edit-outline" width={16} />}
                onClick={() => navigate(siblingPath(location.pathname, CMS_ROUTES.questionBank))}
              >
                Question bank
              </Button>
              {/* The live page, NOT meta.canonicalUrl. That column is an SEO
                  value an editor sets for crawlers and may legitimately name
                  the marketing host, which is not where this page is served. */}
              <Button
                size="small"
                variant="outlined"
                href={PUBLIC_LANDING_URL}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<Icon icon="mdi:open-in-new" width={16} />}
              >
                View page
              </Button>
            </Stack>
          </Stack>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            {TABS.map((t) => (
              <Tab
                key={t.value}
                value={t.value}
                label={t.label}
                icon={<Icon icon={t.icon} width={18} />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            ))}
          </Tabs>

          {tab === 'content' && (
            <SectionPanel sections={sections} revalidateSeconds={revalidateSeconds} />
          )}
          {tab === 'seo' && (
            <SeoPanel
              meta={meta}
              preview={metaPreview}
              options={options}
              revalidateSeconds={revalidateSeconds}
            />
          )}
          {tab === 'testimonials' && (
            <TestimonialsPanel
              testimonials={testimonials}
              revalidateSeconds={revalidateSeconds}
              onChanged={load}
            />
          )}
          {tab === 'settings' && (
            <SettingsPanel settings={settings} revalidateSeconds={revalidateSeconds} />
          )}
        </>
      )}
    </GlobalWrapper>
  );
};

export default LandingPageCms;
